<template>
  <router-view />
</template>

<script>

export default {
  name: 'App',
  components: {
  }
}
</script>

<style>
/* Dark Theme Variables */
:root {
  /* Dark Theme Colors */
  --dark-bg-primary: #0f0f23;
  --dark-bg-secondary: #1a1b26;
  --dark-bg-tertiary: #24283b;
  --dark-bg-card: #2a2e3e;
  --dark-bg: #0c0c0c;
  --darker-bg: #080808;
  --card-bg: #1a1a1a;
  --gold-primary: #d4af37;
  --gold-secondary: #b8941f;
  --gold-light: #f4e4a9;
  --gold-dark: #8b6914;
  --text-gold: #ffd700;
  --text-light: #f8f9fa;
  --text-muted: #a0a0a0;
  --border-gold: rgba(212, 175, 55, 0.3);
  --shadow-gold: rgba(212, 175, 55, 0.4);
  --admin-accent: #d4af37;
  --error-color: #e74c3c;
  --success-color: #2ecc71;

  --card-bg: #1a1a1a;
  --low-color: #2ecc71;
  --normal-color: #3498db;
  --high-color: #f39c12;
  --urgent-color: #e74c3c;

  /* Red Accent Colors */
  --accent-red: #ff2e4c;
  --accent-red-dark: #c91e3a;
  --accent-red-light: #ff5c78;

  /* Text Colors */
  --text-primary: #e2e8f0;
  --text-secondary: #94a3b8;
  --text-muted: #64748b;

  /* Border Colors */
  --border-dark: #374151;
  --border-light: #4b5563;

  /* Status Colors */
  --success: #10b981;
  --warning: #f59e0b;
  --error: #ef4444;
  --info: #3b82f6;

  /* Gradients */
  --gradient-primary: linear-gradient(135deg, var(--accent-red), var(--accent-red-dark));
  --gradient-dark: linear-gradient(135deg, var(--dark-bg-primary), var(--dark-bg-secondary));
  --gradient-card: linear-gradient(145deg, var(--dark-bg-card), var(--dark-bg-tertiary));

  /* Shadows */
  --shadow-dark: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
  --shadow-lg: 0 10px 15px -3px rgba(0, 0, 0, 0.4);
  --shadow-xl: 0 20px 25px -5px rgba(0, 0, 0, 0.5);
}

/* Global Dark Theme Styles */
#app {
  background: var(--dark-bg-primary);
  color: var(--text-primary);
  min-height: 100vh;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
}

/* Smooth transitions for theme switching */
* {
  transition: background-color 0.3s ease, border-color 0.3s ease, color 0.3s ease;
}

/* Scrollbar Styling for Dark Theme */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: var(--dark-bg-secondary);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--accent-red);
  border-radius: 4px;
  border: 2px solid var(--dark-bg-secondary);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--accent-red-light);
}

/* Selection Color */
::selection {
  background: var(--accent-red);
  color: white;
}

::-moz-selection {
  background: var(--accent-red);
  color: white;
}

/* Typography */
h1,
h2,
h3,
h4,
h5,
h6 {
  color: var(--text-primary);
  font-weight: 600;
  margin-bottom: 0.5rem;
}

h1 {
  font-size: 2.5rem;
  background: linear-gradient(135deg, var(--text-primary), var(--accent-red-light));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

h2 {
  font-size: 2rem;
  color: var(--text-primary);
}

h3 {
  font-size: 1.5rem;
  color: var(--text-primary);
}

p {
  color: var(--text-secondary);
  margin-bottom: 1rem;
}

/* Links */
a {
  color: var(--accent-red);
  text-decoration: none;
  transition: all 0.3s ease;
}

a:hover {
  color: var(--accent-red-light);
  text-decoration: none;
}

/* Form Elements */
.form-control {
  background: var(--dark-bg-card);
  border: 1px solid var(--border-dark);
  color: var(--text-primary);
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.3s ease;
}

.form-control:focus {
  background: var(--dark-bg-tertiary);
  border-color: var(--accent-red);
  color: var(--text-primary);
  box-shadow: 0 0 0 3px rgba(255, 46, 76, 0.1);
}

.form-control::placeholder {
  color: var(--text-muted);
}

.form-label {
  color: var(--text-secondary);
  font-weight: 500;
  margin-bottom: 0.5rem;
}

/* Buttons */
.btn {
  border-radius: 8px;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  transition: all 0.3s ease;
  border: none;
  position: relative;
  overflow: hidden;
}

.btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  transition: left 0.5s ease;
}

.btn:hover::before {
  left: 100%;
}

.btn-primary {
  background: var(--gradient-primary);
  color: white;
  box-shadow: var(--shadow-dark);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
  background: linear-gradient(135deg, var(--accent-red-light), var(--accent-red));
}

.btn-secondary {
  background: var(--dark-bg-card);
  color: var(--text-primary);
  border: 1px solid var(--border-dark);
}

.btn-secondary:hover {
  background: var(--dark-bg-tertiary);
  border-color: var(--accent-red);
  transform: translateY(-2px);
}

.btn-success {
  background: linear-gradient(135deg, var(--success), #059669);
  color: white;
}

.btn-danger {
  background: linear-gradient(135deg, var(--error), #dc2626);
  color: white;
}

/* Cards */
.card {
  background: var(--gradient-card);
  border: 1px solid var(--border-dark);
  border-radius: 12px;
  box-shadow: var(--shadow-dark);
  backdrop-filter: blur(10px);
}

.card-header {
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid var(--border-dark);
  padding: 1.25rem;
}

.card-body {
  padding: 1.25rem;
  color: var(--text-secondary);
}

/* Tables */
.table {
  color: var(--text-secondary);
  background: var(--dark-bg-card);
  border-radius: 8px;
  overflow: hidden;
}

.table thead th {
  background: var(--dark-bg-tertiary);
  color: var(--text-primary);
  border-bottom: 2px solid var(--accent-red);
  font-weight: 600;
  padding: 1rem;
}

.table tbody td {
  padding: 1rem;
  border-color: var(--border-dark);
  vertical-align: middle;
}

.table tbody tr {
  transition: all 0.3s ease;
}

.table tbody tr:hover {
  background: rgba(255, 46, 76, 0.05);
  transform: translateX(4px);
}

/* Navigation */
.navbar {
  background: var(--gradient-dark) !important;
  backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-dark);
  box-shadow: var(--shadow-xl);
}

.navbar-brand {
  color: var(--text-primary) !important;
  font-weight: 700;
  font-size: 1.25rem;
}

.nav-link {
  color: var(--text-secondary) !important;
  transition: all 0.3s ease;
  border-radius: 6px;
  margin: 0 0.25rem;
}

.nav-link:hover,
.nav-link.active {
  color: var(--text-primary) !important;
  background: rgba(255, 46, 76, 0.1);
}

/* Sidebar */
.sidebar {
  background: var(--gradient-dark);
  border-right: 1px solid var(--border-dark);
}

.sidebar .nav-link {
  color: var(--text-secondary);
  padding: 0.75rem 1rem;
  margin: 0.125rem 0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.sidebar .nav-link:hover,
.sidebar .nav-link.active {
  color: var(--text-primary);
  background: var(--gradient-primary);
  transform: translateX(4px);
}

/* Modals */
.modal-content {
  background: var(--dark-bg-card);
  border: 1px solid var(--border-dark);
  border-radius: 12px;
  box-shadow: var(--shadow-xl);
}

.modal-header {
  border-bottom: 1px solid var(--border-dark);
  background: var(--dark-bg-tertiary);
}

.modal-footer {
  border-top: 1px solid var(--border-dark);
  background: var(--dark-bg-tertiary);
}

/* Alerts */
.alert {
  border: none;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  backdrop-filter: blur(10px);
}

.alert-success {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success);
  border-left: 4px solid var(--success);
}

.alert-danger {
  background: rgba(239, 68, 68, 0.1);
  color: var(--error);
  border-left: 4px solid var(--error);
}

.alert-warning {
  background: rgba(245, 158, 11, 0.1);
  color: var(--warning);
  border-left: 4px solid var(--warning);
}

.alert-info {
  background: rgba(59, 130, 246, 0.1);
  color: var(--info);
  border-left: 4px solid var(--info);
}

/* Badges */
.badge {
  padding: 0.35em 0.65em;
  font-weight: 500;
  border-radius: 6px;
}

.badge.bg-primary {
  background: var(--gradient-primary) !important;
}

.badge.bg-success {
  background: linear-gradient(135deg, var(--success), #059669) !important;
}

.badge.bg-danger {
  background: linear-gradient(135deg, var(--error), #dc2626) !important;
}

/* Progress Bars */
.progress {
  background: var(--dark-bg-tertiary);
  border-radius: 10px;
  height: 8px;
  overflow: hidden;
}

.progress-bar {
  background: var(--gradient-primary);
  border-radius: 10px;
  transition: width 0.6s ease;
}

/* Loading Spinner */
.spinner-border {
  color: var(--accent-red);
}

/* Custom Utility Classes */
.glass-effect {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.glow-red {
  box-shadow: 0 0 20px rgba(255, 46, 76, 0.3);
}

.glow-blue {
  box-shadow: 0 0 20px rgba(59, 130, 246, 0.3);
}

.text-gradient {
  background: var(--gradient-primary);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* Animation Keyframes */
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideInLeft {
  from {
    opacity: 0;
    transform: translateX(-30px);
  }

  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: 0.7;
  }
}

/* Apply animations */
.fade-in {
  animation: fadeIn 0.6s ease-out;
}

.slide-in-left {
  animation: slideInLeft 0.5s ease-out;
}

.pulse {
  animation: pulse 2s infinite;
}

/* Responsive Design */
@media (max-width: 768px) {
  #app {
    font-size: 14px;
  }

  .container-fluid {
    padding-left: 1rem;
    padding-right: 1rem;
  }

  .btn {
    padding: 0.625rem 1.25rem;
    font-size: 0.875rem;
  }
}

/* Print Styles */
@media print {
  #app {
    background: white !important;
    color: black !important;
  }

  .navbar,
  .sidebar {
    display: none !important;
  }
}

/* Focus States for Accessibility */
button:focus,
input:focus,
select:focus,
textarea:focus {
  outline: 2px solid var(--accent-red);
  outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  :root {
    --dark-bg-primary: #000000;
    --dark-bg-secondary: #111111;
    --text-primary: #ffffff;
    --text-secondary: #cccccc;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  * {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
</style>